'use client'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import { Dispatch, FC, PropsWithChildren, SetStateAction, useState } from 'react'
import { tss } from 'tss-react/mui'
import ActionButton from './ActionButton'

const useStyles = tss.withNestedSelectors<'isSimple'>().create(({ theme, classes }) => ({
    collapsedWithOverlay: {
        '&:before': {
            content: '""',
            position: 'absolute',
            bottom: '100%',
            display: 'block',
            height: 48,
            width: '100%',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff)',
        },
    },
    isSimple: {},
    collapsibleInnerBox: {
        marginBottom: theme.spacing(3),

        [`&.${classes.isSimple}`]: {
            marginBottom: 0,
        },
    },
    buttonWrapper: {
        position: 'relative',
    },
}))

export type CollapsibleProps = PropsWithChildren<{
    actionText?: string
    withOverlay?: boolean
    collapsedHeight?: number
    isSimple?: boolean
}>

const ExpandButton: FC<
    Pick<CollapsibleProps, 'actionText' | 'isSimple'> & {
        isExpanded: boolean
        setExpanded: Dispatch<SetStateAction<boolean>>
    }
> = ({ actionText = 'Show', isExpanded, setExpanded, isSimple = false }) => {
    return (
        <ActionButton
            actionText={actionText}
            isExpanded={isExpanded}
            setExpanded={setExpanded}
            withEndIcon
            isSimple={isSimple}
        />
    )
}

function ExpandButtonWrapper({
    collapsedHeight,
    isExpanded,
    withOverlay,
    children,
}: PropsWithChildren<{
    collapsedHeight?: number
    withOverlay?: boolean
    isExpanded?: boolean
}>) {
    const { classes, cx } = useStyles()

    if (!collapsedHeight) {
        return children
    }

    return (
        <Box className={cx(classes.buttonWrapper, !isExpanded && withOverlay && classes.collapsedWithOverlay)}>
            {children}
        </Box>
    )
}

const Collapsible: FC<CollapsibleProps> = ({
    collapsedHeight = 0,
    children,
    actionText = 'Show',
    withOverlay = false,
    isSimple = false,
}) => {
    const { classes } = useStyles()
    const [isExpanded, setExpanded] = useState(false)

    return (
        <>
            <Collapse in={isExpanded} collapsedSize={collapsedHeight} timeout="auto">
                <Box className={classes.collapsibleInnerBox}>{children}</Box>
            </Collapse>
            <ExpandButtonWrapper collapsedHeight={collapsedHeight} isExpanded={isExpanded} withOverlay={withOverlay}>
                <ExpandButton
                    actionText={actionText}
                    isExpanded={isExpanded}
                    setExpanded={setExpanded}
                    isSimple={isSimple}
                />
            </ExpandButtonWrapper>
        </>
    )
}

export default Collapsible
